/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import moment from 'moment';
import { isEmpty, pluck } from 'ramda';
import resizeImage from 'utils/resizeImage';
import uploadR2File from 'utils/uploadR2File';
import {
  Icon,
  Input,
  Select,
  TextBox,
  ToggleInput,
  ReactSelect,
  AsyncSelect,
  CategoryAdd,
  BrandAdd,
  DepartmentAdd,
  PhotoCropperDialog,
} from '../../index';
import useValidator from '../../../hooks/useValidator';
import { MiscFormActions } from '../../../pages/Misc/Misc';
import {
  Brand,
  Category,
  Department,
  ProductStatus,
  Tax,
} from '../../../context/storeContext';
import { Option } from '../../Inputs/SelectInput/SelectInput';
import { Product } from '../../../pages/Inventory/Inventory';

export type InventoryFormDataType = {
  id?: number;
  name: string;
  description: string;
  notes: string;
  categoryId: Option;
  brandId: Option;
  supplierId: Option;
  departmentId: Option;
  price: number;
  quantity: number;
  upc: string;
  status: string;
  inStockDate: string;
  stockThreshold: number;
  cost: number;
  weight: number;
  trackInventory: boolean;
  trackWholesale: boolean;
  packPrice: number;
  packQuantity: number;
  taxes: string;
  chargeTax?: boolean;
  photos: string | null;
};

type InventoryProps = {
  inventory: Product | undefined;
  statuses: ProductStatus[];
  taxes: Tax[];
  categories: Category[];
  brands: Brand[];
  departments: Department[];
  sku: string;
  onSupplierSearch: (search: string) => Promise<Option[]>;
};

const InventoryForm = React.forwardRef<MiscFormActions, InventoryProps>(
  (
    {
      inventory,
      statuses,
      taxes,
      categories,
      brands,
      departments,
      onSupplierSearch,
      sku,
    },
    ref,
  ) => {
    const [product, setProduct] = React.useState({
      name: inventory?.name || '',
      description: inventory?.description || '',
      notes: inventory?.notes || '',
      categoryId: inventory?.category
        ? {
            label: inventory?.category?.name,
            value: inventory?.category?.id,
          }
        : '',
      brandId: inventory?.brand
        ? {
            label: inventory?.brand?.name,
            value: inventory?.brand?.id,
          }
        : '',

      supplierId: inventory?.supplier
        ? {
            label: inventory?.supplier?.businessName,
            value: inventory?.supplier?.supplierId,
          }
        : '',
      departmentId: inventory?.inventory.department
        ? {
            label: inventory?.inventory.department?.name,
            value: inventory?.inventory.department?.id,
          }
        : '',
      price: inventory?.inventory?.price || '',
      quantity: inventory?.inventory ? inventory?.inventory.quantity : 1,
      upc: inventory?.inventory.upc || '',
      status: inventory?.inventory.status || 'In Stock',
      inStockDate: inventory
        ? moment(inventory.inventory.inStockDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
      stockThreshold: inventory?.inventory.stockThreshold || 0,
      cost: inventory?.inventory?.cost || '',
      weight: inventory?.inventory?.weight || '',
      trackInventory: inventory ? inventory?.inventory.trackInventory : true,
      trackWholesale: inventory ? inventory?.inventory.trackWholesale : false,
      packPrice: inventory?.inventory?.packPrice || '',
      packQuantity: inventory?.inventory.packQuantity || 0,
      taxes: '',
      photos: inventory?.photos?.at(0) || null,
    });
    const [errors, setErrors] = React.useState({
      name: false,
      price: false,
      quantity: false,
      packPrice: false,
      packQuantity: false,
      cost: false,
      weight: false,
      stockThreshold: false,
    });
    const [validator, showValidationMessage] = useValidator({});
    const [chargeTax, setChargeTax] = React.useState(false);
    const [showCategoryAdd, setShowCategoryAdd] = React.useState(false);
    const [showBrandAdd, setShowBrandAdd] = React.useState(false);
    const [showDepartmentAdd, setShowDepartmentAdd] = React.useState(false);
    const [showPhotoCropperModal, setShowPhotoCropperModal] =
      React.useState(false);
    const [uploadedFile, setUploadedFile] = React.useState<File>();
    const [productImageUrl, setProductImageUrl] = React.useState<string>('');
    const [isUploading, setIsUploading] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      let tax = '';

      if (inventory?.taxes && inventory?.taxes.length > 1) {
        // const parentTaxId = inventory?.taxes?.[0]?.parentId || '';
        const parentTaxId = '';

        tax =
          taxes
            .find(fndTax => fndTax.id?.toString() === parentTaxId?.toString())
            ?.id?.toString() || '';
      } else {
        tax = inventory?.taxes?.[0]?.id?.toString() || '';
      }

      setProduct(fndProduct => {
        const updatedProduct = {
          ...fndProduct,
          taxes: tax,
        };

        setChargeTax(Boolean(tax));

        return updatedProduct;
      });
    }, [inventory?.taxes, taxes]);

    React.useImperativeHandle(ref, () => {
      return {
        getFormData: inventoryFormData,
      };
    });

    const handleProductInputChange = (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value, type } = event.target;

      const fndInputValue =
        type !== 'number' ? value : (event.target as any)?.valueAsNumber;

      setProduct(fndProduct => {
        let updatedFndProduct = {
          ...fndProduct,
          [name]: fndInputValue,
        };

        if (
          name === 'quantity' &&
          fndInputValue > 0 &&
          fndProduct.status === 'Low Stock'
        ) {
          updatedFndProduct = {
            ...updatedFndProduct,
            status: 'In Stock',
          };
        }

        if (
          (name === 'packPrice' &&
            !isEmpty(fndInputValue) &&
            fndProduct.packQuantity > 0 &&
            !fndProduct.trackWholesale) ||
          (name === 'packQuantity' &&
            fndInputValue > 0 &&
            !isEmpty(fndProduct.packPrice) &&
            !fndProduct.trackWholesale)
        ) {
          updatedFndProduct = {
            ...updatedFndProduct,
            trackWholesale: true,
          };
        }

        return updatedFndProduct;
      });
    };

    const handleProductPhotoChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const { files } = event.target;
      if (files && files.length > 0) {
        const resizedFile = await resizeImage(files[0], 0.5);
        setUploadedFile(resizedFile);
        setShowPhotoCropperModal(true);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handlePhotoUpload = async (blob: Blob) => {
      if (!uploadedFile) return;

      setIsUploading(true);

      try {
        const fileExtension =
          uploadedFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const fileKey = `${crypto.randomUUID()}.${fileExtension}`;

        // Upload directly to Cloudflare R2
        const publicUrl = await uploadR2File(
          blob,
          fileKey,
          'image/jpeg',
          'products',
        );

        if (!publicUrl) {
          throw new Error('Upload failed - no URL returned');
        }

        // Set the preview URL
        setProductImageUrl(publicUrl);

        // Update product state with the public URL
        setProduct(fndProduct => ({
          ...fndProduct,
          photos: [publicUrl] as any,
        }));

        setShowPhotoCropperModal(false);
        setUploadedFile(undefined);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error uploading product photo to R2:', error);
        // eslint-disable-next-line no-alert
        // window.alert('Failed to upload image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    };

    const inventoryFormData = (): InventoryFormDataType | null => {
      if (!validator.allValid()) {
        setErrors(errs => ({
          ...errs,
          name: !validator.fieldValid('name'),
          price: !validator.fieldValid('price'),
          quantity: !validator.fieldValid('quantity'),
          packPrice: !validator.fieldValid('packPrice'),
          packQuantity: !validator.fieldValid('packQuantity'),
          cost: !validator.fieldValid('cost'),
          weight: !validator.fieldValid('weight'),
          stockThreshold: !validator.fieldValid('stockThreshold'),
        }));
        showValidationMessage(true);
        return null;
      }

      let selectedTaxes = '';

      if (chargeTax) {
        // Does this selected tax have children with it's ID as parent ID
        const withParentIDs = taxes.filter(
          fndTax => fndTax.id.toString() !== product.taxes,
          // && fndTax.parentId?.toString() === product.taxes,
        );
        selectedTaxes =
          withParentIDs.length > 0
            ? pluck('id', withParentIDs).join('|')
            : product.taxes;
      }

      const fndProduct = {
        ...product,
        taxes: selectedTaxes,
        chargeTax,
        ...(inventory && inventory?.id && { id: inventory.id }),
      } as InventoryFormDataType;

      return fndProduct;
    };

    return (
      <>
        <div className="h-full bg-white">
          <form className="w-full h-full flex flex-row space-x-10 items-start">
            <div className="rounded-md w-[75%] border border-[rgba(84,174,255,0.4)]">
              <div
                className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b
            border-[rgba(84,174,255,0.4)]"
              >
                <h2 className="text-xbase font-semibold">Item Details</h2>
              </div>
              <div className="pt-3 pb-5 px-5">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="text-xbase block mb-1">
                      Product name{' '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={product.name}
                      onChange={handleProductInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          name: !validator.check(
                            product.name,
                            'required|min:3',
                          ),
                        }));
                      }}
                      error={errors.name}
                    />
                    {validator.message('name', product.name, 'required|min:3', {
                      messages: {
                        required: 'Product name is required.',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    })}
                  </div>
                  <div>
                    <label
                      htmlFor="description"
                      className="text-xbase block mb-1"
                    >
                      Short description
                    </label>
                    <Input
                      id="description"
                      name="description"
                      value={product.description}
                      onChange={handleProductInputChange}
                    />
                  </div>
                  <div className="flex flex-row space-x-10">
                    <div className="">
                      <label
                        htmlFor="chargeTax"
                        className="text-xbase block mb-1"
                      >
                        Charge Tax
                      </label>
                      <ToggleInput
                        id="chargeTax"
                        name="chargeTax"
                        value="chargeTax"
                        checked={chargeTax}
                        // onChange={event => setChargeTax(event.target.checked)}
                        onChange={() => false}
                        className="align-middle cursor-pointer"
                      />
                    </div>
                    <div>
                      <label htmlFor="taxes" className="text-xbase block mb-1">
                        Tax Rate
                      </label>
                      <Select
                        placeholder={{
                          value: '',
                          label: `${!chargeTax ? 'No Tax' : 'Select tax rate'}`,
                        }}
                        disabled={!chargeTax}
                        value={product.taxes}
                        options={
                          taxes.length > 0
                            ? taxes.map(fndTax => ({
                                value: fndTax.id,
                                label: fndTax.name,
                              }))
                            : []
                        }
                        name="taxes"
                        id="taxes"
                        onChange={handleProductInputChange}
                        className="w-[200px]"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-6 mt-10">
                  <div className="border-b border-[#d0d7de]">
                    <h2 className="text-xbase font-semibold">
                      Pricing & Inventory
                    </h2>
                  </div>
                  <div className="flex flex-row gap-5 !mt-4">
                    <div className="w-[150px] shrink-0">
                      <label htmlFor="price" className="text-xbase block mb-1">
                        Price{' '}
                        <span className="text-xs font-light text-pos-red">
                          *
                        </span>
                      </label>
                      <Input
                        name="price"
                        id="price"
                        value={product.price}
                        min={0}
                        type="number"
                        onChange={handleProductInputChange}
                        onBlur={() => {
                          setErrors(errs => ({
                            ...errs,
                            price: !validator.check(
                              product.price,
                              'required|numeric|min:0,num',
                            ),
                          }));
                        }}
                        error={errors.price}
                      />
                      {validator.message(
                        'price',
                        product.price,
                        'required|numeric|min:0,num',
                        {
                          messages: {
                            required: 'Price is required.',
                          },
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>
                    <div className="w-[100px] shrink-0">
                      <label
                        htmlFor="quantity"
                        className="text-xbase block mb-1"
                      >
                        Qty
                      </label>
                      <Input
                        id="quantity"
                        name="quantity"
                        type="number"
                        min={0}
                        value={product.quantity}
                        disabled={!product.trackInventory}
                        onChange={handleProductInputChange}
                      />
                      {validator.message(
                        'quantity',
                        product.quantity,
                        'numeric|min:0,num',
                        {
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>
                    <div className="flex-1">
                      <label htmlFor="sku" className="text-xbase block mb-1">
                        SKU
                      </label>
                      <Input
                        name="sku"
                        id="sku"
                        value={sku || ''}
                        disabled
                        onChange={() => false}
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor="upc"
                        className="text-xbase block mb-1 whitespace-nowrap"
                      >
                        UPC (Barcode)
                      </label>
                      <Input
                        name="upc"
                        id="upc"
                        value={product.upc}
                        onChange={handleProductInputChange}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row space-x-5">
                    <div>
                      <label htmlFor="weight" className="text-xbase block mb-1">
                        Weight (kg)
                      </label>
                      <Input
                        name="weight"
                        id="weight"
                        type="number"
                        min={0}
                        value={product.weight}
                        onChange={handleProductInputChange}
                        onBlur={() => {
                          setErrors(errs => ({
                            ...errs,
                            weight: !validator.check(
                              product.weight,
                              'numeric|min:0,num',
                            ),
                          }));
                        }}
                        error={errors.weight}
                        className="w-[150px]"
                      />
                      {validator.message(
                        'weight',
                        product.weight,
                        'numeric|min:0,num',
                        {
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="inStockDate"
                        className="text-xbase block mb-1"
                      >
                        In Stock Date
                      </label>
                      <Input
                        id="inStockDate"
                        name="inStockDate"
                        type="date"
                        value={product.inStockDate}
                        onChange={handleProductInputChange}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="stockThreshold"
                        className="text-xbase whitespace-nowrap block"
                      >
                        Low Stock Threshold
                      </label>
                      <Input
                        id="stockThreshold"
                        name="stockThreshold"
                        value={product.stockThreshold}
                        type="number"
                        min={0}
                        onChange={handleProductInputChange}
                        disabled={!product.trackInventory}
                        onBlur={() => {
                          setErrors(errs => ({
                            ...errs,
                            stockThreshold: !validator.check(
                              product.stockThreshold,
                              'numeric|min:0,num',
                            ),
                          }));
                        }}
                        error={errors.stockThreshold}
                        className="w-[100px]"
                      />
                      {validator.message(
                        'stockThreshold',
                        product.stockThreshold,
                        'numeric|min:0,num',
                        {
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>
                    <div>
                      <label htmlFor="cost" className="text-xbase block mb-1">
                        Cost Price
                      </label>
                      <Input
                        name="cost"
                        id="cost"
                        type="number"
                        min={0}
                        value={product.cost}
                        onChange={handleProductInputChange}
                        onBlur={() => {
                          setErrors(errs => ({
                            ...errs,
                            cost: !validator.check(
                              product.cost,
                              'numeric|min:0,num',
                            ),
                          }));
                        }}
                        className="w-[150px]"
                        error={errors.cost}
                      />
                      {validator.message(
                        'cost',
                        product.cost,
                        'numeric|min:0,num',
                        {
                          className:
                            'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                        },
                      )}
                    </div>
                  </div>
                  <div className="flex flex-row space-x-10">
                    <div className="">
                      <label
                        htmlFor="trackInventory"
                        className="text-xbase block mb-1"
                      >
                        Track Inventory
                      </label>
                      <ToggleInput
                        id="trackInventory"
                        name="trackInventory"
                        value={product.trackInventory.toString()}
                        checked={product.trackInventory}
                        onChange={event =>
                          setProduct(fndProduct => ({
                            ...fndProduct,
                            trackInventory: event.target.checked,
                            quantity: !event.target.checked
                              ? 1
                              : fndProduct.quantity,
                            status: !event.target.checked
                              ? 'In Stock'
                              : fndProduct.status,
                            trackWholesale: !event.target.checked
                              ? false
                              : fndProduct.trackWholesale,
                            packPrice: !event.target.checked
                              ? ''
                              : fndProduct.packPrice,
                            packQuantity: !event.target.checked
                              ? 0
                              : fndProduct.packQuantity,
                          }))
                        }
                        className="align-middle cursor-pointer"
                      />
                    </div>
                    <div>
                      <label htmlFor="status" className="text-xbase block mb-1">
                        Status
                      </label>
                      <Select
                        placeholder={{
                          value: '',
                          label: '',
                        }}
                        value={product.status}
                        options={
                          statuses.length > 0
                            ? statuses.map(fndStatus => ({
                                value: fndStatus.name,
                                label: fndStatus.name,
                              }))
                            : []
                        }
                        name="status"
                        id="status"
                        className={`w-[200px]
                        ${
                          product.status === 'In Stock'
                            ? '!bg-green-200 !text-pos-green !border-pos-green '
                            : product.status === 'Low Stock'
                            ? 'bg-red-200 !text-pos-red !border-pos-red'
                            : ''
                        }
                        disabled:!border-pos-green disabled:!bg-green-200 disabled:!opacity-100`}
                        disabled={!inventory}
                        onChange={handleProductInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-4 mt-10">
                  <div className="border-b border-[#d0d7de]">
                    <h2 className="text-xbase font-semibold inline-flex items-center">
                      Wholesale
                      <span
                        className="leading-0 text-[10px] w-3 h-3 bg-[#666] rounded-full inline-flex
                    items-center justify-center text-white ml-1 cursor-help -mt-[3px]"
                      >
                        ?
                      </span>
                    </h2>
                  </div>
                  <div className="flex flex-row !mt-4">
                    <div className="">
                      <label
                        htmlFor="trackWholesale"
                        className="text-xbase block mb-1"
                      >
                        Track Wholesale
                      </label>
                      <ToggleInput
                        id="trackWholesale"
                        name="trackWholesale"
                        value={product.trackWholesale.toString()}
                        checked={
                          product.trackWholesale && product.trackInventory
                        }
                        onChange={event =>
                          product.trackInventory &&
                          setProduct(fndProduct => ({
                            ...fndProduct,
                            trackWholesale: event.target.checked,
                          }))
                        }
                        className="align-middle cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="flex flex-row space-x-5">
                    <div>
                      <label
                        htmlFor="packPrice"
                        className="text-xbase block mb-1"
                      >
                        Price of carton
                      </label>
                      <Input
                        name="packPrice"
                        id="packPrice"
                        value={product.packPrice}
                        type="number"
                        min={0}
                        onChange={handleProductInputChange}
                        disabled={!product.trackInventory}
                        className="w-[150px]"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="packQuantity"
                        className="text-xbase block mb-1"
                      >
                        Quantity in Carton
                      </label>
                      <Input
                        name="packQuantity"
                        id="packQuantity"
                        type="number"
                        min={0}
                        value={product.packQuantity}
                        onChange={handleProductInputChange}
                        disabled={!product.trackInventory}
                        className="w-[150px]"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <div className="border-b border-[#d0d7de]">
                    <h2 className="text-xbase">Product notes</h2>
                  </div>
                  <div className="flex flex-row mt-6">
                    <TextBox
                      id="notes"
                      name="notes"
                      value={product.notes}
                      onChange={handleProductInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="w-[25%] flex flex-col">
              <div className="">
                <div className="h-fit pt-2 pb-1">
                  <h2 className="text-xbase">Supplier</h2>
                </div>
                <div className="flex flex-row justify-between items-center">
                  <AsyncSelect
                    name="supplierId"
                    id="supplierId"
                    placeholder="Select supplier"
                    noOptionsMessage={() => 'No supplier found!'}
                    cacheOptions
                    isClearable
                    loadOptions={onSupplierSearch}
                    value={product.supplierId}
                    onChange={option =>
                      setProduct(fndProduct => ({
                        ...fndProduct,
                        supplierId: option,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="mt-6 pt-2 border-t border-t-[hsla(210,18%,87%,1)]">
                <div className="h-fit pt-2 pb-1">
                  <h2 className="text-xbase">Product Category</h2>
                </div>
                <div className="flex flex-row justify-between space-x-3 items-center">
                  <ReactSelect
                    name="categoryId"
                    id="categoryId"
                    placeholder="Select category"
                    noOptionsMessage={() => 'No category found!'}
                    value={product.categoryId}
                    isClearable
                    onChange={option =>
                      setProduct(fndProduct => ({
                        ...fndProduct,
                        categoryId: option,
                      }))
                    }
                    options={
                      categories.length > 0
                        ? categories.map(fndCategory => ({
                            value: fndCategory.id,
                            label: fndCategory.name,
                          }))
                        : []
                    }
                  />
                  <button
                    onClick={() => setShowCategoryAdd(!showCategoryAdd)}
                    type="button"
                    className="w-[40px] h-[40px] rounded-md shrink-0 flex items-center justify-center
                  bg-[#f6f8fa] !text-pos-black text-xbase border border-[rgba(27,31,36,0.15)]
                  shadow-[0_1px_0_rgba(27,31,36,0.04),inset_0_1px_0_rgba(255,255,255,0.25)] font-medium"
                  >
                    <Icon
                      id="plusSign"
                      className="w-[14px] h-[14px] stroke-pos-black fill-pos-black inline-flex"
                    />
                  </button>
                </div>
              </div>
              <div className="mt-6 pt-2 border-t border-t-[hsla(210,18%,87%,1)]">
                <div className="h-fit pt-2 pb-1">
                  <h2 className="text-xbase">Product Brand</h2>
                </div>
                <div className="flex flex-row justify-between space-x-3 items-center">
                  <ReactSelect
                    name="brandId"
                    id="brandId"
                    placeholder="Select brand"
                    noOptionsMessage={() => 'No brand found!'}
                    value={product.brandId}
                    isClearable
                    onChange={option =>
                      setProduct(fndProduct => ({
                        ...fndProduct,
                        brandId: option,
                      }))
                    }
                    options={
                      brands.length > 0
                        ? brands.map(fndBrand => ({
                            value: fndBrand.id,
                            label: fndBrand.name,
                          }))
                        : []
                    }
                  />
                  <button
                    onClick={() => setShowBrandAdd(!showBrandAdd)}
                    type="button"
                    className="w-[40px] h-[40px] rounded-md shrink-0 flex items-center justify-center
                  bg-[#f6f8fa] !text-pos-black text-xbase border border-[rgba(27,31,36,0.15)]
                  shadow-[0_1px_0_rgba(27,31,36,0.04),inset_0_1px_0_rgba(255,255,255,0.25)] font-medium"
                  >
                    <Icon
                      id="plusSign"
                      className="w-[14px] h-[14px] stroke-pos-black fill-pos-black inline-flex"
                    />
                  </button>
                </div>
              </div>
              <div className="mt-6 pt-2 border-t border-t-[hsla(210,18%,87%,1)]">
                <div className="h-fit pt-2 pb-1">
                  <h2 className="text-xbase">Department</h2>
                </div>
                <div className="flex flex-row justify-between space-x-3 items-center">
                  <ReactSelect
                    name="departmentId"
                    id="departmentId"
                    placeholder="Select department"
                    noOptionsMessage={() => 'No department found!'}
                    value={product.departmentId}
                    isClearable
                    onChange={option =>
                      setProduct(fndProduct => ({
                        ...fndProduct,
                        departmentId: option,
                      }))
                    }
                    options={
                      departments.length > 0
                        ? departments.map(fndDepartment => ({
                            value: fndDepartment.id,
                            label: fndDepartment.name,
                          }))
                        : []
                    }
                  />
                  <button
                    onClick={() => setShowDepartmentAdd(!showDepartmentAdd)}
                    type="button"
                    className="w-[40px] h-[40px] rounded-md shrink-0 flex items-center justify-center
                  bg-[#f6f8fa] !text-pos-black text-xbase border border-[rgba(27,31,36,0.15)]
                  shadow-[0_1px_0_rgba(27,31,36,0.04),inset_0_1px_0_rgba(255,255,255,0.25)] font-medium"
                  >
                    <Icon
                      id="plusSign"
                      className="w-[14px] h-[14px] stroke-pos-black fill-pos-black inline-flex"
                    />
                  </button>
                </div>
              </div>
              <div className="mt-6 pt-2 border-t border-t-[hsla(210,18%,87%,1)]">
                <div className="h-fit pt-2 pb-1">
                  <h2 className="text-xbase">Product Photo</h2>
                </div>
                <div className="relative w-full">
                  {!(productImageUrl || product.photos) ? (
                    <label
                      htmlFor="productPhoto"
                      className="flex flex-col p-4 items-center justify-center w-52 h-52 border-2 border-dashed 
                      border-gray-400 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <img
                          src="/assets/icons/icon-product-upload.png"
                          alt="Upload icon"
                          className="w-20 h-20 mb-2 object-contain"
                        />
                        <p className="text-xbase text-center font-semibold text-gray-700">
                          ADD PRODUCT PHOTO
                        </p>
                        <p className="text-sm text-center text-gray-500">
                          Click to upload or drag & drop.
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        id="productPhoto"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProductPhotoChange}
                        disabled={isUploading}
                      />
                    </label>
                  ) : (
                    <div className="relative w-full">
                      <div className="relative w-52 h-52 rounded-lg overflow-hidden border-2 border-gray-300">
                        {isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                            <Icon
                              id="loader"
                              className="w-10 h-10 text-pos-green"
                            />
                          </div>
                        )}
                        <img
                          src={productImageUrl || product.photos || ''}
                          alt="Product"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (fileInputRef.current) {
                              fileInputRef.current.click();
                            }
                          }}
                          disabled={isUploading}
                          className="absolute right-2 bottom-2 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-gray-700 hover:bg-pos-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Change product photo"
                        >
                          <img
                            src="/assets/icons/icon-product-upload.png"
                            alt="Upload icon"
                            className="h-5 w-5 self-center object-contain brightness-0 invert"
                          />
                        </button>
                        <input
                          ref={fileInputRef}
                          id="productPhoto"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleProductPhotoChange}
                          disabled={isUploading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        {showCategoryAdd && (
          <CategoryAdd
            onCloseModal={() => setShowCategoryAdd(false)}
            categories={categories}
            onSetAddedValue={option =>
              setProduct(fndProduct => ({
                ...fndProduct,
                categoryId: {
                  label: option.label,
                  value: option.value as number,
                },
              }))
            }
          />
        )}

        {showBrandAdd && (
          <BrandAdd
            onCloseModal={() => setShowBrandAdd(false)}
            brands={brands}
            onSetAddedValue={option =>
              setProduct(fndProduct => ({
                ...fndProduct,
                brandId: {
                  label: option.label,
                  value: option.value as number,
                },
              }))
            }
          />
        )}

        {showDepartmentAdd && (
          <DepartmentAdd
            onCloseModal={() => setShowDepartmentAdd(false)}
            departments={departments}
            onSetAddedValue={option =>
              setProduct(fndProduct => ({
                ...fndProduct,
                departmentId: {
                  label: option.label,
                  value: option.value as number,
                },
              }))
            }
          />
        )}

        {showPhotoCropperModal && (
          <PhotoCropperDialog
            photo={uploadedFile}
            open={showPhotoCropperModal}
            onClose={() => {
              setShowPhotoCropperModal(false);
              setUploadedFile(undefined);
            }}
            onSetCroppedPhoto={handlePhotoUpload}
            isUploading={isUploading}
          />
        )}
      </>
    );
  },
);

export default InventoryForm;
